<script setup lang="ts">
/** Aufklappbare Gruppe im Eigenschaften-Panel, optional mit Hinweistext. Zustand wird je Gruppe gemerkt. */
import { ref, watch } from 'vue'
import { ChevronDown, Info } from 'lucide-vue-next'

const props = withDefaults(defineProps<{ id: string; title: string; hint?: string; defaultOpen?: boolean }>(), { hint: '', defaultOpen: true })

const STORAGE_KEY = 'pfe-design-pp-open'
function readState(): Record<string, boolean> {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {} } catch { return {} }
}
const stored = readState()[props.id]
const open = ref(typeof stored === 'boolean' ? stored : props.defaultOpen)
watch(open, (v) => {
  try {
    const s = readState()
    s[props.id] = v
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s))
  } catch { /* privater Modus o. Ä. – Zustand dann nur für diese Sitzung */ }
})
const bodyId = `pp-sec-${props.id}`
</script>

<template>
  <section class="sec" :class="{ 'sec--open': open }">
    <button type="button" class="sec__head" :aria-expanded="open" :aria-controls="bodyId" @click="open = !open">
      <span class="sec__title">{{ title }}</span>
      <ChevronDown class="sec__chev" :size="16" aria-hidden="true" />
    </button>
    <div v-show="open" :id="bodyId" class="sec__body">
      <p v-if="hint" class="sec__hint"><Info :size="14" aria-hidden="true" /><span>{{ hint }}</span></p>
      <slot />
    </div>
  </section>
</template>

<style scoped>
.sec { border-top: 1px solid rgba(15, 23, 42, 0.08); min-width: 0; }
.sec__head { display: flex; align-items: center; justify-content: space-between; gap: 8px; width: 100%; padding: 12px 0; border: 0; background: transparent; font: inherit; font-size: 13px; font-weight: 700; color: #0f172a; cursor: pointer; text-align: left; }
.sec__head:focus-visible { outline: 2px solid var(--pfe-color-accent, #e30b17); outline-offset: 2px; border-radius: 4px; }
.sec__chev { flex: none; color: rgba(15, 23, 42, 0.5); transition: transform 150ms ease; transform: rotate(-90deg); }
.sec--open .sec__chev { transform: rotate(0deg); }
.sec__body { display: grid; grid-template-columns: minmax(0, 1fr); gap: 10px; padding-bottom: 14px; min-width: 0; }
.sec__hint { display: flex; gap: 6px; align-items: flex-start; margin: 0; padding: 8px 10px; border-radius: 6px; background: rgba(15, 23, 42, 0.04); color: rgba(15, 23, 42, 0.68); font-size: 12px; line-height: 1.4; }
.sec__hint svg { flex: none; margin-top: 1px; color: rgba(15, 23, 42, 0.5); }
@media (prefers-reduced-motion: reduce) { .sec__chev { transition: none; } }
</style>
